import React, { useEffect, useState } from "react";
import { Button, Col, Figure, Form, Modal, Row } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { ValidationMessage } from "../../../utils/constants/validationMessage";
import ODatePicker from "../../../components/shared/datePicker/ODatePicker";
import { whiteSpaceCheck } from "../../../helper/helper";
import { ErrorMessage } from "@hookform/error-message";
import { addAndUpdateEvent } from "../../../hooks/ApisContainer/Event";
import useToastContext from "../../../hooks/useToastContext";
import { BASE_URL } from "../../../services/path";
import moment from "moment";
import { Loader } from "lucide-react";

const AddEdit = ({
  show,
  handleShowModal,
  fetchedGetEvents,
  eventDetail = {},
}) => {
  const notification = useToastContext();

  // HOOK FORM
  const {
    register,
    setValue,
    clearErrors,
    watch,
    formState: { errors },
    handleSubmit,
  } = useForm();

  const allowedTypes = ["image/jpeg", "image/png", "image/jpg"];
  const [eventDate, setEventDate] = useState("");
  const [eventDateError, setEventDateError] = useState("");
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [startTimeError, setStartTimeError] = useState("");
  const [endTimeError, setEndTimeError] = useState("");
  const [diffErr, setDiffErr] = useState("");
  const [eventImages, setEventImages] = useState([]);
  const [deleteEventImages, setDeleteEventImages] = useState([]);
  const [loader, setLoader] = useState(false);

  const onSubmit = (data) => {
    try {
      const isValid = dateValidation(eventDate, startTime, endTime);
      const payload = {
        modeType: "add",
      };
      const diffTime = `${moment(startTime).format("hh:mm A")} - ${moment(
        endTime
      ).format("hh:mm A")}`;

      if (Object.keys(eventDetail).length > 0) {
        payload.modeType = "edit";
        payload.id = eventDetail._id;
      }
      const formData = new FormData();
      formData.append("name", data?.name || "");
      formData.append("description", data?.description || "");
      formData.append("date", eventDate || "");
      formData.append("time", diffTime);
      formData.append("address", data?.address || "");
      formData.append(
        "deleteEventImages",
        JSON.stringify(
          deleteEventImages.map((item) => item.split(`${BASE_URL}/uploads/`)[1])
        )
      );

      if (data?.headerImage?.[0])
        formData.append("headerImage", data?.headerImage[0] || "");
      if (Array.from(eventImages || [])?.length > 0)
        Array.from(eventImages).forEach((item, index) => {
          typeof item === "object" && formData.append(`eventImages`, item);
        });
      if (isValid) {
        addAndUpdateEvent(
          formData,
          payload,
          notification,
          (loading, error, response) => {
            setLoader(loading);
            if (!error) {
              fetchedGetEvents({}, true);
              handleShowModal();
            }
          }
        );
      } else {
        console.log("Something went wrong.");
      }
    } catch (err) {
      console.error("Error", err);
    }
  };

  const handleChangeEventDate = (dates, index) => {
    const eventDates = [...dates];
    setEventDate(eventDates);
    dateValidation(...dates);
  };

  const dateValidation = (date) => {
    let dateError = "";

    if (!date) {
      dateError = "Date field is required.";
    } else {
      dateError = "";
    }

    if (dateError) {
      setEventDateError(dateError);
      return false;
    } else {
      setEventDateError(dateError);
      return true;
    }
  };

  const dateValidationStartEnd = (startTime, endTime) => {
    let dateStartError = "";
    let dateEndError = "";

    if (!startTime) {
      dateStartError = "Start time is required.";
    } else {
      dateStartError = "";
    }

    if (!endTime) {
      dateEndError = "End time is required.";
    } else {
      dateEndError = "";
    }

    if (dateStartError && dateEndError) {
      setStartTimeError(dateStartError);
      setEndTimeError(dateEndError);
      return true;
    } else {
      setStartTimeError(dateStartError);
      setEndTimeError(dateEndError);
      return false;
    }
  };

  const handleRemoveImage = (item, index) => {
    if (typeof item === "object") {
      setEventImages(eventImages.filter((el, i) => i !== index));
    } else {
      setEventImages(eventImages.filter((el, i) => i !== index));
      setDeleteEventImages([
        ...eventImages.filter((el, i) => i === index),
        ...deleteEventImages,
      ]);
    }
  };

  const handleStartTimeChange = (time) => {
    setStartTime(time);
    validateTimeRange(time, endTime);
    dateValidationStartEnd(time, endTime);
  };

  const handleEndTimeChange = (time) => {
    setEndTime(time);
    validateTimeRange(startTime, time);
    dateValidationStartEnd(startTime, time);
  };

  const validateTimeRange = (start, end) => {
    if (start && end && new Date(start) >= new Date(end)) {
      setDiffErr("End time must be after start time.");
    } else {
      setDiffErr("");
    }
  };

  const parseTimesToDate = (date, timeRange) => {
    if (!date || !timeRange) return null;

    // Split the time range into start and end times
    const [firstTime, secondTime] = timeRange.split(" - ");

    if (!firstTime || !secondTime) {
      console.error("Invalid time range format:", timeRange);
      return null;
    }

    // Helper function to combine date and time
    const combineDateAndTime = (date, time) => {
      const combined = new Date(date);
      const [hourString, minuteString] = time
        .match(/\d{1,2}:\d{2}/)[0]
        .split(":");
      const isPM = time.includes("PM");
      let hours = parseInt(hourString, 10);

      // Convert to 24-hour format
      if (isPM && hours !== 12) {
        hours += 12;
      } else if (!isPM && hours === 12) {
        hours = 0; // Handle midnight
      }

      combined.setHours(hours);
      combined.setMinutes(parseInt(minuteString, 10));
      combined.setSeconds(0);
      combined.setMilliseconds(0);
      return combined;
    };

    setStartTime(combineDateAndTime(date, firstTime));
    setEndTime(combineDateAndTime(date, secondTime));
  };

  useEffect(() => {
    if (
      [...Array.from(watch("eventImages") || [])].every((item) =>
        typeof item === "object" ? allowedTypes.includes(item.type) : true
      )
    )
      setEventImages([
        ...eventImages,
        ...Array.from(watch("eventImages") || []),
      ]);
  }, [watch("eventImages")]);

  useEffect(() => {
    if (Object.keys(eventDetail).length > 0) {
      parseTimesToDate(eventDetail.date, eventDetail.time);
      setEventDate(eventDetail.date);
      setValue("name", eventDetail.name);
      setValue("description", eventDetail.description);
      setValue("address", eventDetail.address);
      setValue("headerImage", eventDetail.headerImage);
      setValue(
        "eventImages",
        eventDetail.eventImages?.map((item) => BASE_URL + "/uploads/" + item)
      );
    }
  }, [eventDetail]);

  return (
    <Form className="common-modal">
      <Modal show={show} onHide={handleShowModal}>
        <Form onSubmit={handleSubmit(onSubmit)} noValidate autoComplete="off">
          <Modal.Header closeButton className="py-2">
            <Modal.Title className="fw-bold fs-5">Add Event</Modal.Title>
          </Modal.Header>
          <Modal.Body className="p-4">
            <Row>
              <Col md={12} className="mb-3">
                <Form.Group>
                  <Form.Label className="fw-bold">
                    Event Scheduled Date *
                  </Form.Label>
                  <ODatePicker
                    name="date"
                    value={eventDate && new Date(eventDate)}
                    placeholder={"Select event date."}
                    onChange={(dates) => handleChangeEventDate(dates)}
                    dateError={eventDateError}
                    className="form-control"
                  />
                </Form.Group>
              </Col>
              <Col md={6} className="mb-3">
                <Form.Group>
                  <Form.Label className="fw-bold">Start Time *</Form.Label>
                  <ODatePicker
                    name="start-time-picker"
                    value={startTime}
                    placeholder={"Select Start Time"}
                    handleDateChange={handleStartTimeChange}
                    dateError={startTimeError}
                    className="form-control"
                    options={{
                      noCalendar: true, // Disable calendar
                      enableTime: true,
                      dateFormat: "H:i", // Time only format
                      time_24hr: true,
                    }}
                  />
                </Form.Group>
              </Col>
              <Col md={6} className="mb-3">
                <Form.Group>
                  <Form.Label className="fw-bold">End Time *</Form.Label>
                  <ODatePicker
                    name="end-time-picker"
                    value={endTime}
                    placeholder={"Select End Time"}
                    handleDateChange={handleEndTimeChange}
                    dateError={diffErr || endTimeError}
                    className="form-control"
                    options={{
                      noCalendar: true, // Disable calendar
                      enableTime: true,
                      dateFormat: "H:i", // Time only format
                      time_24hr: true,
                    }}
                  />
                </Form.Group>
              </Col>

              <Col md={12} className="mb-3">
                <Form.Group>
                  <Form.Label className="fw-bold">Event Name *</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter event name"
                    name="name"
                    {...register(`name`, {
                      required: ValidationMessage.EVENT_NAME_REQUIRED,
                      validate: (value) => whiteSpaceCheck(value),
                    })}
                    // maxLength={40}
                  />
                  <ErrorMessage
                    errors={errors}
                    name={`name`}
                    render={({ message }) => (
                      <p className="error-message text-start py-2 px-1 text-danger">
                        {message}
                      </p>
                    )}
                  />
                </Form.Group>
              </Col>
              <Col md={12} className="mb-3">
                <Form.Group>
                  <Form.Label className="fw-bold">Description *</Form.Label>
                  <Form.Control
                    as="textarea"
                    placeholder="Enter description"
                    rows={3}
                    name="description"
                    {...register(`description`, {
                      required: ValidationMessage.EVENT_DESCRIPTION_REQUIRED,
                      validate: (value) => whiteSpaceCheck(value),
                    })}
                    // maxLength={40}
                  />
                  <ErrorMessage
                    errors={errors}
                    name={`description`}
                    render={({ message }) => (
                      <p className="error-message text-start py-2 px-1 text-danger">
                        {message}
                      </p>
                    )}
                  />
                </Form.Group>
              </Col>

              <Col md={12} className="mb-3">
                <Form.Group>
                  <Form.Label className="fw-bold">Event Address *</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter event address"
                    name="address"
                    {...register(`address`, {
                      required: ValidationMessage.EVENT_ADDRESS_REQUIRED,
                      validate: (value) => whiteSpaceCheck(value),
                    })}
                    // maxLength={40}
                  />
                  <ErrorMessage
                    errors={errors}
                    name={`address`}
                    render={({ message }) => (
                      <p className="error-message text-start py-2 px-1 text-danger">
                        {message}
                      </p>
                    )}
                  />
                </Form.Group>
              </Col>

              <Col md={12} className="mb-3">
                <Form.Group>
                  <Form.Label className="fw-bold">
                    Event Header Image *
                  </Form.Label>
                  <Form.Control
                    type="file"
                    accept=".jpg, .jpeg, .png"
                    name="headerImage"
                    {...register(`headerImage`, {
                      required: eventDetail?.headerImage
                        ? false
                        : ValidationMessage.EVENT_HEADER_IMAGE_REQUIRED,
                      // required: ValidationMessage.EVENT_HEADER_IMAGE_REQUIRED,
                      validate: (fileList) => {
                        const file = fileList[0];
                        const allowedTypes = [
                          "image/png",
                          "image/jpg",
                          "image/jpeg",
                        ];
                        if (!file && !eventDetail?.headerImage)
                          return ValidationMessage.EVENT_HEADER_IMAGE_REQUIRED;
                        if (
                          file &&
                          typeof file === "object" &&
                          !allowedTypes.includes(file.type)
                        ) {
                          return "Only JPEG, JPG, and PNG files are allowed.";
                        }
                        if (
                          file &&
                          typeof file === "object" &&
                          file.size > 300 * 1024
                        ) {
                          return "Please upload an image smaller than 300 KB.";
                        }

                        return true;
                      },
                    })}
                  />
                  <ErrorMessage
                    errors={errors}
                    name={`headerImage`}
                    render={({ message }) => (
                      <p className="error-message text-start py-2 px-1 text-danger">
                        {message}
                      </p>
                    )}
                  />
                </Form.Group>
              </Col>
              <Col md={12} className="mb-3">
                <Form.Group>
                  <Form.Label className="fw-bold">Event Images *</Form.Label>
                  <Form.Control
                    type="file"
                    multiple={true}
                    accept=".jpg, .jpeg, .png"
                    name="eventImages"
                    {...register(`eventImages`, {
                      required:
                        eventImages.length === 0
                          ? ValidationMessage.EVENT_IMAGE_REQUIRED
                          : false,
                      // required: ValidationMessage.EVENT_IMAGE_REQUIRED,
                      validate: (fileList) => {
                        const file = Array.from(fileList || []);
                        if (eventImages?.length === 0 || file.length > 0) {
                          if (!file)
                            return ValidationMessage.EVENT_IMAGE_REQUIRED;
                          if (
                            !file.every((item) =>
                              typeof item === "object"
                                ? allowedTypes.includes(item.type)
                                : true
                            )
                          ) {
                            return "Only JPEG, JPG, and PNG files are allowed.";
                          }
                          if (
                            !file.every((item) =>
                              typeof item === "object"
                                ? (item.size < 300 * 1024)
                                : true
                            )
                          ) {
                            return "Please upload an image smaller than 300 KB.";
                          }
                        }
                        return true;
                      },
                    })}
                    // maxLength={40}
                  />
                  <ErrorMessage
                    errors={errors}
                    name={`eventImages`}
                    render={({ message }) => (
                      <p className="error-message text-start py-2 px-1 text-danger">
                        {message}
                      </p>
                    )}
                  />
                </Form.Group>
              </Col>
              <Row>
                {eventImages.map((item, index) => (
                  <Col
                    key={index}
                    md={2}
                    className="mb-3"
                    style={{ position: "relative" }}
                  >
                    <Button
                      className="bg-transparent border-0 p-0 ms-auto d-block"
                      style={{
                        position: "absolute",
                        right: "19px",
                        top: "-12px",
                      }}
                      type="button"
                      onClick={() => handleRemoveImage(item, index)}
                    >
                      <i className="bi bi-dash-circle-fill text-red-button-2"></i>
                    </Button>
                    <Figure.Image
                      src={
                        typeof item === "object"
                          ? URL.createObjectURL(item)
                          : item
                      }
                      alt="No_Image"
                      className="profile-image-event"
                      style={{ height: "40px", width: "40px" }}
                    />
                  </Col>
                ))}
              </Row>
            </Row>
          </Modal.Body>
          <Modal.Footer className="justify-content-center">
            <Button
              type="submit"
              onClick={() => {
                dateValidation(eventDate);
                dateValidationStartEnd(startTime, endTime);
              }}
              variant="primary"
              className="bg-orange border-0"
              disabled={loader}
              style={loader ? { cursor: "not-allowed" } : { cursor: "pointer" }}
            >
              Submit{" "}
              {loader && (
                <Loader
                  style={{
                    marginLeft: "20px",
                  }}
                  className="loader"
                />
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </Form>
  );
};

export default AddEdit;
